(() => {
  'use strict';

  const SETTINGS = (window.themeVariables && window.themeVariables.settings) || {};
  if (SETTINGS.enableMarketingParamCapture === false) {
    return;
  }

  /* WH-172: bounded/retrying fetch with plain-fetch fallback; the attributes
     update goes through cartMutate (idempotent, reconciled before retry). */
  const ehFetch = (url, opts) =>
    window.EH && window.EH.fetchRetry ? window.EH.fetchRetry(url, opts) : fetch(url, opts);

  const STORAGE_KEY = 'everhope_marketing_params';
  const TS_KEY = 'everhope_marketing_params_ts';
  const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
  const DEBOUNCE_MS = 250; 

  // Cart-attribute keys add a leading underscore so they're hidden from the
  // checkout UI but persist on the Shopify order (Shopify convention).
  const PARAM_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'gbraid', 'wbraid', 'dclid',
    'fbclid', 'fb_action_ids', 'fb_action_types', 'fb_source',
    'msclkid', 'ttclid', 'li_fat_id',
    'mc_cid', 'mc_eid', 'irclickid'
  ];

  function safeGet(storage, key) {
    try { return storage.getItem(key); } catch (_) { return null; }
  }
  function safeSet(storage, key, value) {
    try { storage.setItem(key, value); } catch (_) { /* private mode etc. */ }
  }
  function safeRemove(storage, key) {
    try { storage.removeItem(key); } catch (_) { /* noop */ }
  }

  function readStored() {
    const ts = parseInt(safeGet(localStorage, TS_KEY), 10);
    if (ts && Date.now() - ts > EXPIRY_MS) {
      safeRemove(localStorage, STORAGE_KEY);
      safeRemove(localStorage, TS_KEY);
      safeRemove(sessionStorage, STORAGE_KEY);
      return {};
    }
    const raw = safeGet(localStorage, STORAGE_KEY) || safeGet(sessionStorage, STORAGE_KEY);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (_) {
      return {};
    }
  }

  // stampFirstTouch=false writes the blob without starting the 30-day clock —
  // see readEhId(): eh_id is an identity, not a marketing touch, and letting it
  // start the clock would expire a UTM captured weeks later almost immediately.
  function writeStored(obj, stampFirstTouch) {
    const json = JSON.stringify(obj);
    safeSet(localStorage, STORAGE_KEY, json);
    safeSet(sessionStorage, STORAGE_KEY, json);
    // First-touch model: stamp the timestamp only on the very first capture so
    // the 30-day clock starts at the original touch, not at the most recent visit.
    if (stampFirstTouch !== false && !safeGet(localStorage, TS_KEY)) {
      safeSet(localStorage, TS_KEY, String(Date.now()));
    }
  }

  function captureFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const stored = readStored();
    let changed = false;
    for (const key of PARAM_KEYS) {
      const value = params.get(key);
      if (!value) continue;
      const storedKey = '_' + key;
      if (stored[storedKey] != null && stored[storedKey] !== '') continue;
      stored[storedKey] = value;
      changed = true;
    }
    if (changed) writeStored(stored);
    return stored;
  }

  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  /* ── WH-328: eh_id — the owned identity key minted on .everhope.care ───────
     store.everhope.care is a subdomain and the cookie is scoped
     Domain=.everhope.care, so it already arrives on every request — the cookie
     is live truth. ?eh_id= is the fallback for cookie-partitioned contexts
     (in-app webviews); cache it so later pageviews without the param still
     stamp the cart. Never minted here — the store is downstream of the site. */
  const EH_ID_ATTR = '_eh_id';
  const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  /* Only the cookie proves the id belongs to THIS browser. A ?eh_id= can be
     forwarded (a shared WhatsApp link, an indexed URL), so it may carry someone
     else's id — fine as an attribution hint, never as an identity assertion:
     PostHog person merges cannot be undone. Consumers that assert identity use
     this; everything else uses readEhId(). */
  function readFirstPartyEhId() {
    const cookie = readCookie('eh_id');
    return UUID_SHAPE.test(cookie || '') ? cookie : null;
  }

  function readEhId() {
    const cookie = readFirstPartyEhId();
    if (cookie) return cookie;
    // The URL param is visitor-supplied: shape-check before it reaches an order.
    const param = new URLSearchParams(window.location.search).get('eh_id');
    if (UUID_SHAPE.test(param || '')) {
      const stored = readStored();
      if (stored[EH_ID_ATTR] !== param) {
        stored[EH_ID_ATTR] = param;
        // false: an identity handoff must not start the attribution clock.
        writeStored(stored, false);
      }
      return param;
    }
    const cached = readStored()[EH_ID_ATTR];
    return UUID_SHAPE.test(cached || '') ? cached : null;
  }

  // _ga cookie: "GA1.1.<cid>.<ts>" → GA4 client_id is "<cid>.<ts>".
  function readGaCid() {
    const raw = readCookie('_ga');
    if (!raw) return null;
    const parts = raw.split('.');
    if (parts.length < 4) return null;
    return parts.slice(2).join('.');
  }

  // _ga_<STREAM> cookie. Two known formats:
  //   GS1.1.<sid>.<count>.<engaged>.<ts>            (legacy, dot-separated)
  //   GS2.1.s<sid>$o<count>$g<engaged>$t<ts>$...    (current, $-separated subfields)
  // First-match is correct because this storefront runs a single GA4 web stream.
  // Revisit if a second GA4 property is ever provisioned on the same domain.
  function readGaSid() {
    const cookies = document.cookie.split(';');
    for (const c of cookies) {
      const trimmed = c.trim();
      if (!/^_ga_[A-Z0-9]+=/.test(trimmed)) continue;
      const value = decodeURIComponent(trimmed.split('=').slice(1).join('='));
      let m = value.match(/^GS2\.\d+\.s(\d+)/);
      if (m) return m[1];
      m = value.match(/^GS1\.\d+\.(\d+)/);
      if (m) return m[1];
    }
    return null;
  }

  function buildCartAttributes() {
    const out = {};
    const stored = readStored();
    for (const key of PARAM_KEYS) {
      const v = stored['_' + key];
      if (v) out['_' + key] = v;
    }
    // Omit GA keys rather than sending empty strings when cookies aren't set yet.
    const cid = readGaCid();
    const sid = readGaSid();
    if (cid) out._ga_cid = cid;
    if (sid) out._ga_sid = sid;
    // WH-328: durable join key — every order carries eh_id, so a purchase resolves
    // to the site/pathfinder journey even when client events are blocked.
    const ehId = readEhId();
    if (ehId) out[EH_ID_ATTR] = ehId;
    // WH-154: carry PostHog's distinct_id into checkout (a separate sandbox that
    // can't read the storefront PostHog cookie) so browse→purchase resolves to
    // one person. Only when PostHog is loaded; skipped silently otherwise.
    try {
      if (window.posthog && typeof window.posthog.get_distinct_id === 'function') {
        const phId = window.posthog.get_distinct_id();
        if (phId) out._ph_distinct_id = phId;
      }
    } catch (_) { /* PostHog not ready yet — the next cart event retries */ }
    return out;
  }

  function attributesMatch(current, desired) {
    for (const key of Object.keys(desired)) {
      if (current[key] !== desired[key]) return false;
    }
    return true;
  }

  async function syncCartAttributes() {
    const desired = buildCartAttributes();
    if (Object.keys(desired).length === 0) return;
    try {
      const cartRes = await ehFetch('/cart.js', { credentials: 'same-origin' });
      if (!cartRes.ok) return;
      const cart = await cartRes.json();
      const current = (cart && cart.attributes) || {};
      if (attributesMatch(current, desired)) return;
      if (window.EH && window.EH.cartMutate) {
        await window.EH.cartMutate('/cart/update.js', { attributes: desired }, {
          fetchOptions: { credentials: 'same-origin', keepalive: true }
        });
      } else {
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: desired })
        });
      }
    } catch (_) {
      // Best-effort: never block cart flows on an attribution write failure.
    }
  }

  function debounce(fn, delay) {
    let timer = null;
    return function debounced() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, delay);
    };
  }

  const onCartEvent = debounce(syncCartAttributes, DEBOUNCE_MS);

  function init() {
    captureFromUrl();
    readEhId(); // caches ?eh_id= at the landing, before navigation drops the param
    // Vendor theme.js dispatches "cart:refresh"; the custom *-custom.js files
    // dispatch "cart:change". Listening on document catches both.
    document.addEventListener('cart:refresh', onCartEvent);
    document.addEventListener('cart:change', onCartEvent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.everhopeMarketingParams = {
    read: readStored,
    ehId: readEhId,
    ehIdFirstParty: readFirstPartyEhId,
    readCart: buildCartAttributes,
    sync: syncCartAttributes,
    clear: function clear() {
      safeRemove(localStorage, STORAGE_KEY);
      safeRemove(localStorage, TS_KEY);
      safeRemove(sessionStorage, STORAGE_KEY);
    }
  };
})();
/* ── Facebook in-app browser: checkout fix ──
   When settings.enable_inapp_browser_redirect is on, Android users are redirected
   to their native browser by an early inline script in theme.liquid. For iOS (where
   forced redirect isn't possible) — and for Android when that redirect is turned off —
   window.open(_top) replaces the top frame and bypasses FBIAB's navigation block on
   both form POSTs and async JS callbacks. */
(function () {
  if (!/FBAN|FBAV|FB_IAB|Instagram/.test(navigator.userAgent)) return;

  function goCheckoutFBIAB() {
    window.open('/checkout', '_top');
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector('[name="checkout"]')) return;
    e.preventDefault();
    goCheckoutFBIAB();
  }, true);

  window._fbiabGoCheckout = goCheckoutFBIAB;
})();
